// First-party session refresh for sandbox and portal: renews the ~10-minute access JWT via the HttpOnly refresh
// cookie (eng-docs "AD - Session Refresh Tokens").

/** Every Dolfin AuthResponse carries at least this. */
export interface RefreshedAuth {
  token: string;
  userId: string;
}

export type RefreshOutcome<R extends RefreshedAuth> =
  /** A usable access token. `response` is absent when another tab had already refreshed. */
  | { kind: "refreshed"; token: string; response?: R }
  /** The refresh cookie is gone, expired or revoked: the user has to sign in again. */
  | { kind: "ended" }
  /** Network or server trouble. Keep the session; a later attempt may succeed. */
  | { kind: "unavailable" };

export interface SessionRefresherOptions<S extends { token: string; user: { userId: string } }, R extends RefreshedAuth> {
  /** localStorage key of the app's stored session. */
  storageKey: string;
  /** Merge a fresh AuthResponse into the stored session (null when nothing is stored yet). Null ends the session. */
  apply: (stored: S | null, response: R) => S | null;
  /** Defaults to the same-origin proxy route. */
  refreshUrl?: string;
  logoutUrl?: string;
}

/** Seconds before `exp` at which a token counts as due for refresh. */
const EXPIRY_MARGIN_MS = 60_000;

export function jwtExpiresAt(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True when the token is expired or will be within the refresh margin. */
export function isTokenDue(token: string, now = Date.now()): boolean {
  const exp = jwtExpiresAt(token);
  return exp === null || exp - EXPIRY_MARGIN_MS <= now;
}

export interface SessionRefresher<R extends RefreshedAuth> {
  /** Fresh access token. Pass the failed token: if another tab already refreshed, that token is returned without rotating.
   * One request per tab and a Web Lock across tabs, because every refresh rotates the cookie. */
  refresh: (staleToken: string | null) => Promise<RefreshOutcome<R>>;
  /** Revoke the refresh cookie server-side. Never throws. */
  logout: () => Promise<void>;
  /**
   * fetch() that attaches the current token via `withToken`, and on a 401 refreshes once and replays the request.
   * When the session has ended it returns the original 401 response.
   */
  fetchWithRefresh: (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    withToken: (headers: Headers, token: string | null) => void,
  ) => Promise<Response>;
  /** Called after any change this module made to the stored session (refreshed or ended). */
  subscribe: (listener: () => void) => () => void;
  readStoredToken: () => string | null;
}

export function createSessionRefresher<S extends { token: string; user: { userId: string } }, R extends RefreshedAuth>(
  options: SessionRefresherOptions<S, R>,
): SessionRefresher<R> {
  const refreshUrl = options.refreshUrl ?? "/api/proxy/v1/auth/refresh";
  const logoutUrl = options.logoutUrl ?? "/api/proxy/v1/auth/logout";
  const lockName = `${options.storageKey}:refresh`;
  const listeners = new Set<() => void>();
  let inflight: Promise<RefreshOutcome<R>> | null = null;

  const readStored = (): S | null => {
    try {
      const raw = localStorage.getItem(options.storageKey);
      return raw ? (JSON.parse(raw) as S) : null;
    } catch {
      return null;
    }
  };

  const notify = () => listeners.forEach((l) => l());

  const withLock = <T>(fn: () => Promise<T>): Promise<T> =>
    typeof navigator !== "undefined" && navigator.locks
      ? navigator.locks.request(lockName, fn)
      : fn();

  const doRefresh = async (staleToken: string | null): Promise<RefreshOutcome<R>> => {
    const stored = readStored();
    if (stored?.token && stored.token !== staleToken && !isTokenDue(stored.token)) {
      return { kind: "refreshed", token: stored.token };
    }

    let resp: Response;
    try {
      resp = await fetch(refreshUrl, { method: "POST", credentials: "same-origin" });
    } catch {
      return { kind: "unavailable" };
    }

    const end = (): RefreshOutcome<R> => {
      localStorage.removeItem(options.storageKey);
      notify();
      return { kind: "ended" };
    };
    if (resp.status === 401) return end();
    if (!resp.ok) return { kind: "unavailable" };

    const response = (await resp.json()) as R;
    const current = readStored();
    // The refresh cookie belongs to the host, not this app, so it can come back as another user (apps share localhost).
    // Never switch users: end this session and leave the cookie alone.
    if (current && current.user?.userId !== response.userId) return end();
    const next = options.apply(current, response);
    if (!next) return end();
    localStorage.setItem(options.storageKey, JSON.stringify(next));
    notify();
    return { kind: "refreshed", token: response.token, response };
  };

  const refresh = (staleToken: string | null) => {
    inflight ??= withLock(() => doRefresh(staleToken)).finally(() => {
      inflight = null;
    });
    return inflight;
  };

  const readStoredToken = () => readStored()?.token ?? null;

  return {
    refresh,
    readStoredToken,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    logout: async () => {
      try {
        await fetch(logoutUrl, { method: "POST", credentials: "same-origin" });
      } catch {
        // Signing out locally still happens; the cookie simply idles out.
      }
    },
    fetchWithRefresh: async (input, init, withToken) => {
      let token = readStoredToken();
      if (token && isTokenDue(token)) {
        const outcome = await refresh(token);
        if (outcome.kind === "refreshed") token = outcome.token;
      }

      // Kept so the body can be sent a second time if the first attempt 401s.
      const request = new Request(input, init);
      const retry = request.clone();
      withToken(request.headers, token);
      const response = await fetch(request);
      if (response.status !== 401 || !token) return response;

      const outcome = await refresh(token);
      if (outcome.kind !== "refreshed") return response;
      withToken(retry.headers, outcome.token);
      return fetch(retry);
    },
  };
}
